import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { NeevCloudMark } from '../components/Brand';
import { NewSuggestionModal } from '../components/NewSuggestionModal';
import { ParallaxBackground } from '../components/ParallaxBackground';
import { SuggestionCard } from '../components/SuggestionCard';
import { SuggestionDrawer } from '../components/SuggestionDrawer';
import {
  Avatar,
  CardSkeleton,
  EmptyState,
  ErrorState,
  InboxIcon,
  LogoutIcon,
  PlusIcon,
  ThemeToggle,
} from '../components/primitives';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { api, ApiError, filtersToQuery } from '../lib/api';
import { PRODUCT_THEME, PRODUCTS } from '../lib/products';
import { cx } from '../lib/format';
import {
  EMPTY_FILTERS,
  type ListFilters,
  type Stats,
  type Suggestion,
  type User,
} from '../lib/types';

const PAGE_SIZE = 24;

export function DashboardPage() {
  const { user, logout } = useAuth();
  const toast = useToast();

  const [filters, setFilters] = useState<ListFilters>(EMPTY_FILTERS);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<Stats | null>(null);
  const [people, setPeople] = useState<User[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const isAdmin = user?.role === 'admin';
  // Distinguishes the first load (skeletons) from a filter change (dimming).
  const hasLoadedOnce = useRef(false);

  /* --- roster: only needed for assignment UI ---------------------------- */
  useEffect(() => {
    let cancelled = false;
    api
      .users()
      .then((r) => {
        if (!cancelled) setPeople(r.users);
      })
      .catch(() => {
        // Non-fatal: the board still works, the assignee picker just stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* --- list + stats ----------------------------------------------------- */
  useEffect(() => {
    const controller = new AbortController();
    const query = filtersToQuery(filters, PAGE_SIZE);

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [page, s] = await Promise.all([
          api.list(query, controller.signal),
          api.stats(controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setItems(page.items);
        setTotal(page.total);
        setTotalPages(page.totalPages);
        setStats(s);
        hasLoadedOnce.current = true;
      } catch (err) {
        if (controller.signal.aborted || (err as Error).name === 'AbortError') return;
        setError(
          err instanceof ApiError ? err.message : 'Could not load the board. Please try again.',
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [filters, refreshNonce]);

  const patchFilters = useCallback((patch: Partial<ListFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
  }, []);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const goToPage = useCallback((page: number) => {
    setFilters((f) => ({ ...f, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const showSkeletons = loading && !hasLoadedOnce.current;
  const filtersActive =
    filters.product.length > 0 ||
    filters.category.length > 0 ||
    filters.status.length > 0 ||
    filters.visibility !== '' ||
    filters.assignedToId !== '' ||
    filters.minPriority > 0 ||
    filters.scope !== 'all' ||
    filters.q.trim() !== '';

  return (
    <div className="relative min-h-dvh">
      <ParallaxBackground />

      {/* ------------------------------------------------------------ nav */}
      <header className="hairline sticky top-0 z-40 border-b bg-ink-950/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[92rem] items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <NeevCloudMark size={30} className="shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-mist-50">Suggestion Box</p>
              <p className="hidden text-[0.6875rem] tracking-wide text-mist-600 sm:block">
                NeevCloud
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="btn-primary text-xs sm:text-sm"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">New suggestion</span>
              <span className="sm:hidden">New</span>
            </button>

            <ThemeToggle />

            {user && (
              <div className="hairline flex items-center gap-2 border-l pl-2 sm:pl-3">
                <Avatar user={user} size={30} />
                <div className="hidden min-w-0 lg:block">
                  <p className="truncate text-xs font-medium text-mist-200">{user.name}</p>
                  <p className="text-[0.6875rem] text-mist-600 capitalize">{user.role}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void logout();
                    toast('Signed out.', 'info');
                  }}
                  className="btn-subtle p-2"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogoutIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[92rem] px-4 pt-8 pb-24 sm:px-6">
        {/* --------------------------------------------------------- hero */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8"
        >
          <p className="text-xs font-semibold tracking-[0.24em] text-lotus-300 uppercase">
            {greeting()}, {user?.name.split(' ')[0]}
          </p>
          <h1 className="mt-2 text-h1 font-bold text-mist-50 text-balance">
            What should we make better?
          </h1>
          <p className="mt-2 max-w-2xl text-[0.9375rem] leading-relaxed text-mist-400 text-pretty">
            {isAdmin
              ? 'You are seeing every suggestion across every product, including private ones.'
              : 'You are seeing public suggestions, plus anything you submitted or that is assigned to you.'}
          </p>
        </motion.section>

        {/* -------------------------------------------------------- stats */}
        <StatRow stats={stats} loading={showSkeletons} filters={filters} onFilter={patchFilters} />

        {/* ------------------------------------------------------ filters */}
        <div className="mt-6">
          <FilterBar
            filters={filters}
            onChange={patchFilters}
            people={people}
            isAdmin={Boolean(isAdmin)}
            resultCount={total}
          />
        </div>

        {/* --------------------------------------------------------- grid */}
        <section className="mt-6" aria-live="polite" aria-busy={loading}>
          {error ? (
            <ErrorState message={error} onRetry={refresh} />
          ) : showSkeletons ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : items.length === 0 ? (
            filtersActive ? (
              <EmptyState
                title="Nothing matches those filters"
                message="Try widening the search, or clear the filters to see the whole board again."
                action={
                  <button
                    type="button"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="btn-ghost"
                  >
                    Clear all filters
                  </button>
                }
              />
            ) : (
              <EmptyState
                icon={<InboxIcon className="h-6 w-6" />}
                title="The box is empty"
                message="Nobody has shared a suggestion yet. Be the first — a bug you spotted, something a customer said, or an idea worth building."
                action={
                  <button
                    type="button"
                    onClick={() => setComposerOpen(true)}
                    className="btn-primary"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Share the first suggestion
                  </button>
                }
              />
            )
          ) : (
            <>
              <motion.div
                layout
                className={cx(
                  'grid gap-4 transition-opacity duration-200 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
                  // Dim rather than blank out while refetching — keeps context.
                  loading && 'pointer-events-none opacity-55',
                )}
              >
                <AnimatePresence mode="popLayout">
                  {items.map((s, i) => (
                    <SuggestionCard key={s.id} suggestion={s} onOpen={setOpenId} index={i} />
                  ))}
                </AnimatePresence>
              </motion.div>

              {totalPages > 1 && (
                <nav
                  className="mt-8 flex items-center justify-center gap-2"
                  aria-label="Pagination"
                >
                  <button
                    type="button"
                    onClick={() => goToPage(filters.page - 1)}
                    disabled={filters.page <= 1}
                    className="btn-ghost text-xs"
                  >
                    Previous
                  </button>
                  <span className="px-3 text-xs text-mist-500 tabular-nums">
                    Page {filters.page} of {totalPages}
                    {total !== null && ` · ${total} total`}
                  </span>
                  <button
                    type="button"
                    onClick={() => goToPage(filters.page + 1)}
                    disabled={filters.page >= totalPages}
                    className="btn-ghost text-xs"
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </section>
      </main>

      <NewSuggestionModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={refresh}
        people={people}
        isAdmin={Boolean(isAdmin)}
      />

      <SuggestionDrawer
        id={openId}
        onClose={() => setOpenId(null)}
        onMutated={refresh}
        people={people}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tiles                                                                  */
/* -------------------------------------------------------------------------- */

function StatRow({
  stats,
  loading,
  filters,
  onFilter,
}: {
  stats: Stats | null;
  loading: boolean;
  filters: ListFilters;
  onFilter: (patch: Partial<ListFilters>) => void;
}) {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="surface h-24 p-4">
            <div className="skeleton h-3 w-20" />
            <div className="skeleton mt-3 h-7 w-12" />
          </div>
        ))}
      </div>
    );
  }

  const tiles = [
    {
      label: 'Open suggestions',
      value: (stats.byStatus.new ?? 0) + (stats.byStatus.in_progress ?? 0),
      hint: `${stats.total} total`,
      accent: 'text-mist-50',
      active: false,
      onClick: () => onFilter({ status: ['new', 'in_progress'], page: 1 }),
    },
    {
      label: 'Assigned to you',
      value: stats.assignedToMe,
      hint: 'awaiting your action',
      accent: 'text-status-progress',
      active: filters.scope === 'assigned_to_me',
      onClick: () =>
        onFilter({
          scope: filters.scope === 'assigned_to_me' ? 'all' : 'assigned_to_me',
          page: 1,
        }),
    },
    {
      label: 'High impact',
      value: stats.highPriority,
      hint: 'unresolved, impact 4+',
      accent: 'text-[#f472b6]',
      active: filters.minPriority >= 4,
      onClick: () => onFilter({ minPriority: filters.minPriority >= 4 ? 0 : 4, page: 1 }),
    },
    {
      label: 'Resolved',
      value: stats.byStatus.resolved ?? 0,
      hint: 'shipped or closed out',
      accent: 'text-status-resolved',
      active: filters.status.length === 1 && filters.status[0] === 'resolved',
      onClick: () =>
        onFilter({
          status:
            filters.status.length === 1 && filters.status[0] === 'resolved' ? [] : ['resolved'],
          page: 1,
        }),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((t, i) => (
          <motion.button
            key={t.label}
            type="button"
            onClick={t.onClick}
            aria-pressed={t.active}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -2 }}
            className={cx(
              'surface p-4 text-left transition-colors duration-200 hover:border-[var(--hairline-strong)]',
              t.active && 'border-lotus-400/45 bg-lotus-400/8',
            )}
          >
            <p className="text-xs font-medium tracking-wide text-mist-500">{t.label}</p>
            <p className={cx('mt-2 text-3xl font-bold tabular-nums', t.accent)}>{t.value}</p>
            <p className="mt-0.5 text-[0.6875rem] text-mist-600">{t.hint}</p>
          </motion.button>
        ))}
      </div>

      {/* Per-product breakdown doubles as a quick product filter. */}
      <div className="flex flex-wrap gap-2">
        {PRODUCTS.map((p) => {
          const t = PRODUCT_THEME[p];
          const count = stats.byProduct[p] ?? 0;
          const active = filters.product.includes(p);
          return (
            <button
              key={p}
              type="button"
              onClick={() =>
                onFilter({
                  product: active
                    ? filters.product.filter((x) => x !== p)
                    : [...filters.product, p],
                  page: 1,
                })
              }
              aria-pressed={active}
              className={cx(
                'chip cursor-pointer transition-all duration-150',
                active ? t.chip : 'chip-idle text-mist-500 hover:text-mist-200',
              )}
            >
              <span className={cx('h-1.5 w-1.5 rounded-full', t.dot)} />
              {t.label}
              <span className="ml-0.5 tabular-nums opacity-60">{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
